import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { withCapacitor } from 'Util/Capacitors/hoc';
import { useGetFeedbackTemplate, useGiveFeedback, COMPONENT_TYPES } from 'Util/FeedBackService';
import { ClassroomSubsection, ClassroomInformation } from 'Domain/Classroom';
import { secondsToTime } from 'Util/datetime';
import { FeedbackReportModal } from 'Components/Feedback';
import { SvgIcon } from 'Components/Base';
import { useClassroomInterruption, useRestorePlayerFocus } from 'Util/Hooks';
import useClassroomVideoPlayer from 'Util/Hooks/useClassroomVideoPlayer';

import { REPORT_STATE } from './constant';

const reportIconToggle = <SvgIcon icon={SvgIcon.ICON_LIST.REPORT} color="white" size={24} />;

const FeedbackReport = ({
  capacitorContext: { deviceInfo },
  toggleComponent: toggleComponentProp,
}) => {
  const {
    data: templateData,
    loading,
    isSuccess: isGetTemplateSuccess,
  } = useGetFeedbackTemplate({
    templateId: 'REPORT_FEEDBACK',
  });

  const [{ timeSnapshot, reportState }, setReportState] = useState({
    timeSnapshot: null,
    reportState: REPORT_STATE.INITIALIZED,
  });

  const { giveFeedback, loading: isGiveFeedbackLoading } = useGiveFeedback({
    templateId: 'REPORT_FEEDBACK',
    onCompleted: () => setReportState({ timeSnapshot: null, reportState: REPORT_STATE.COMPLETED }),
  });

  const { activateInterruption, deactivateInterruption } = useClassroomInterruption();
  const { getCurrentTime } = useClassroomVideoPlayer();
  const restorePlayerFocus = useRestorePlayerFocus();

  if (!isGetTemplateSuccess) return null;

  const snapshotVideoTime = () => {
    activateInterruption();
    const currentTimeSecond = getCurrentTime();
    const timeSnapshot = secondsToTime(currentTimeSecond);
    setReportState({ timeSnapshot, reportState: REPORT_STATE.FILLING_FORM });
  };

  const handleCloseModal = () => {
    if (reportState === REPORT_STATE.COMPLETED) {
      setReportState({ timeSnapshot: null, reportState: REPORT_STATE.INITIALIZED });
    }
    deactivateInterruption();
    restorePlayerFocus();
  };

  let options = [];
  const inputComponents = templateData?.getTemplate?.components;

  if (Array.isArray(inputComponents) && inputComponents.length) {
    const selectionComponent = inputComponents.find(
      (component) => component.type === COMPONENT_TYPES.CHOICE
    );
    options =
      selectionComponent?.options?.map((option) => ({
        label: option.title,
        value: option.value,
        disabled: option.value === null ? true : false,
      })) ?? [];
  }

  const toggleComponent = toggleComponentProp ? toggleComponentProp : reportIconToggle;

  return (
    <ClassroomInformation>
      {({ courseCode }) => (
        <ClassroomSubsection>
          {({
            title,
            contentType,
            classroomCourseId,
            currentSubsectionId,
            courseId,
            userProfileData,
          }) => {
            const meta = {
              classroomCourseId,
              courseId,
              courseCode,
              title,
              timeSnapshot,
              ...deviceInfo,
            };
            const userId = userProfileData?.userId;
            return (
              <FeedbackReportModal
                toggleComponent={toggleComponent}
                onOpenModal={snapshotVideoTime}
                onCloseModal={handleCloseModal}
                options={options}
                loading={loading || isGiveFeedbackLoading}
                isSuccess={reportState === REPORT_STATE.COMPLETED}
                onSubmit={(formValue) => {
                  giveFeedback(currentSubsectionId, userId, meta, contentType, formValue);
                }}
              />
            );
          }}
        </ClassroomSubsection>
      )}
    </ClassroomInformation>
  );
};

FeedbackReport.propTypes = {
  toggleComponent: PropTypes.element,
};

export default withCapacitor(FeedbackReport);
